import React from 'react';
import { Brain, CaretRight } from '@phosphor-icons/react';
import { Text, Flex, TooltipTrigger, Tooltip } from '@chatsdet/ui';
import { motion, AnimatePresence } from 'framer-motion';
import type { WorkflowRunThoughtDto } from '../hooks/useWorkflowRunThoughts';

type StepThoughtsProps = {
  thoughts: WorkflowRunThoughtDto[];
  /** Expand by default */
  defaultExpanded?: boolean;
};

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/**
 * Truncate thought content for preview, preserving word boundaries.
 */
function truncateContent(content: string, maxLength = 200): string {
  if (content.length <= maxLength) return content;
  const truncated = content.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > maxLength * 0.7 ? truncated.slice(0, lastSpace) : truncated) + '...';
}

/**
 * Individual thought bubble with timeline connector.
 */
function ThoughtBubble({
  thought,
  isLast,
  index,
}: {
  thought: WorkflowRunThoughtDto;
  isLast: boolean;
  index: number;
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isLong = thought.content.length > 200;

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
      className={`relative pl-5 ${!isLast ? 'pb-3' : ''}`}
    >
      {/* Vertical connector line */}
      {!isLast && <div className="absolute left-[7px] top-5 bottom-0 w-px bg-border/60" />}

      {/* Timeline dot */}
      <div className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full bg-muted border-2 border-border flex items-center justify-center">
        <Brain size={8} weight="fill" className="text-muted-foreground" />
      </div>

      {/* Content bubble */}
      <div className="bg-muted/30 border border-border/60 rounded-lg px-3 py-2">
        {/* Header: timestamp + tool */}
        <Flex gap="sm" align="center" className="mb-1.5">
          <Text size="xs" className="text-muted-foreground font-mono tabular-nums">
            {formatTime(thought.createdAt)}
          </Text>
          {thought.toolName && (
            <TooltipTrigger delay={200}>
              <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono cursor-help">
                {thought.toolName}
              </span>
              <Tooltip>Tool: {thought.toolName}</Tooltip>
            </TooltipTrigger>
          )}
        </Flex>

        {/* Thought content */}
        <Text size="xs" className="text-foreground/90 leading-relaxed whitespace-pre-wrap">
          {isLong && !isExpanded ? truncateContent(thought.content) : thought.content}
        </Text>

        {/* Show more/less toggle */}
        {isLong && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-1.5 text-[10px] text-muted-foreground hover:text-foreground font-medium"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </motion.div>
  );
}

/**
 * Expandable list of agent thoughts for a workflow step.
 * Displays as a collapsible timeline with chat-style bubbles.
 */
export function StepThoughts({ thoughts, defaultExpanded = false }: StepThoughtsProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  if (thoughts.length === 0) {
    return null;
  }

  return (
    <div className="ml-4 mt-1.5">
      {/* Expandable header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors py-1 px-1.5 -ml-1.5 rounded-md hover:bg-muted/50 group"
      >
        <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
          <CaretRight size={11} className="text-muted-foreground group-hover:text-foreground" />
        </motion.div>
        <Brain size={14} weight="duotone" className="text-muted-foreground" />
        <Text size="xs" className="text-muted-foreground group-hover:text-foreground">
          {thoughts.length} thought{thoughts.length !== 1 ? 's' : ''}
        </Text>
      </button>

      {/* Thoughts list with animation */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-2 ml-1 space-y-0">
              {thoughts.map((thought, idx) => (
                <ThoughtBubble
                  key={thought.id}
                  thought={thought}
                  isLast={idx === thoughts.length - 1}
                  index={idx}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
